import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { categoryGuides } from '@/data/categoryGuides';

const guideCategoryMeta: Record<string, { icon: string; meta: string; cta: string }> = {
  suit: {
    icon: 'ri-user-star-line',
    meta: '6문항 · 약 90초',
    cta: '예복 기준 찾기',
  },
  jewelry: {
    icon: 'ri-star-line',
    meta: '5문항 · 약 60초',
    cta: '예물 기준 찾기',
  },
  hanbok: {
    icon: 'ri-heart-line',
    meta: '5문항 · 약 60초',
    cta: '한복 기준 찾기',
  },
};

export default function GuideHub() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const categories = Object.entries(categoryGuides).map(([key, config]) => ({
    key,
    config,
    meta: guideCategoryMeta[key] || { icon: 'ri-guide-line', meta: '', cta: '시작하기' },
  }));

  const closeMenu = useCallback(() => setMobileMenuOpen(false), []);

  // Body scroll lock
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    if (mobileMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-dvh flex flex-col bg-bg-alt pt-12">
      {/* ===== Hub Header ===== */}
      <header className="sticky top-0 z-50 h-14 md:h-16 bg-static-white border-b border-line-divider flex items-center px-5 md:px-6 flex-shrink-0" style={{display: 'none'}}>
        {/* Left: Brand */}
        <Link
          to="/"
          className="flex items-center cursor-pointer"
        >
          <span className="text-label1 md:text-headline2 font-semibold text-label-normal whitespace-nowrap">
            다이렉트 인증업체
          </span>
        </Link>

        {/* Right: Desktop links */}
        <div className="hidden md:flex items-center gap-4 ml-auto">
          <Link
            to="/dress"
            className="text-label1 font-medium text-label-alt hover:text-label-normal transition-colors whitespace-nowrap cursor-pointer"
          >
            인증업체 보기
          </Link>
          <Link
            to="/guide"
            className="inline-flex items-center h-9 px-5 rounded-full bg-primary-normal text-static-white text-label1 font-semibold hover:bg-primary-strong transition-colors whitespace-nowrap cursor-pointer"
          >
            내 취향 찾기
          </Link>
        </div>

        {/* Mobile: Hamburger */}
        <button
          className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-full hover:bg-fill-alt transition-colors cursor-pointer"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="메뉴 열기"
        >
          <i className="ri-menu-line text-label-normal text-xl"></i>
        </button>
      </header>

      {/* ===== Mobile Drawer Dim ===== */}
      <div
        className={`fixed inset-0 z-40 md:hidden bg-black/35 cursor-pointer ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ transition: 'opacity 200ms ease' }}
        onClick={closeMenu}
      />

      {/* ===== Mobile Drawer Panel ===== */}
      <div
        className="fixed top-0 right-0 z-50 h-full md:hidden bg-static-white flex flex-col"
        style={{
          width: 'min(84vw, 360px)',
          height: '100dvh',
          transition: 'transform 220ms ease-out',
          transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
          boxShadow: mobileMenuOpen ? '-2px 0 16px rgba(0,0,0,0.06)' : 'none',
        }}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between h-14 px-5 border-b border-line-divider flex-shrink-0">
          <span className="text-label1 font-semibold text-label-normal">다이렉트 인증업체</span>
          <button
            onClick={closeMenu}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-fill-alt transition-colors cursor-pointer"
            aria-label="메뉴 닫기"
          >
            <i className="ri-close-line text-label-normal text-xl"></i>
          </button>
        </div>

        {/* Drawer Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-5 pt-6 pb-4">
            {/* ---- Home ---- */}
            <Link
              to="/"
              onClick={closeMenu}
              className="flex items-center h-12 px-3 rounded-lg text-label1 text-label-neutral hover:bg-fill-alt transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line text-lg mr-3"></i>
              홈
            </Link>

            {/* ---- 인증업체 ---- */}
            <p className="text-caption text-label-assistive font-medium mt-7 mb-2 px-3 tracking-wide">
              인증업체
            </p>
            <Link
              to="/dress"
              onClick={closeMenu}
              className="flex items-center h-12 px-3 rounded-lg text-label1 text-label-neutral hover:bg-fill-alt transition-colors cursor-pointer"
            >
              <span className="w-7 flex-shrink-0 text-sm mr-1">
                <i className="ri-checkbox-circle-line text-accent-brown"></i>
              </span>
              예복 인증업체
            </Link>
            <Link
              to="/dress"
              onClick={closeMenu}
              className="flex items-center h-12 px-3 rounded-lg text-label1 text-label-neutral hover:bg-fill-alt transition-colors cursor-pointer"
            >
              <span className="w-7 flex-shrink-0 text-sm mr-1">
                <i className="ri-checkbox-circle-line text-accent-brown"></i>
              </span>
              예물 인증업체
            </Link>
            <Link
              to="/dress"
              onClick={closeMenu}
              className="flex items-center h-12 px-3 rounded-lg text-label1 text-label-neutral hover:bg-fill-alt transition-colors cursor-pointer"
            >
              <span className="w-7 flex-shrink-0 text-sm mr-1">
                <i className="ri-checkbox-circle-line text-accent-brown"></i>
              </span>
              한복 인증업체
            </Link>

            {/* ---- 내 취향 찾기 ---- */}
            <p className="text-caption text-label-assistive font-medium mt-7 mb-2 px-3 tracking-wide">
              내 취향 찾기
            </p>
            <Link
              to="/guide/suit"
              onClick={closeMenu}
              className="flex items-center h-12 px-3 rounded-lg text-label1 text-label-neutral hover:bg-fill-alt transition-colors cursor-pointer"
            >
              <span className="w-7 flex-shrink-0 text-sm mr-1">
                <i className="ri-compass-3-line text-label-alt"></i>
              </span>
              예복 취향 찾기
            </Link>
            <Link
              to="/guide/jewelry"
              onClick={closeMenu}
              className="flex items-center h-12 px-3 rounded-lg text-label1 text-label-neutral hover:bg-fill-alt transition-colors cursor-pointer"
            >
              <span className="w-7 flex-shrink-0 text-sm mr-1">
                <i className="ri-compass-3-line text-label-alt"></i>
              </span>
              예물 취향 찾기
            </Link>
            <Link
              to="/guide/hanbok"
              onClick={closeMenu}
              className="flex items-center h-12 px-3 rounded-lg text-label1 text-label-neutral hover:bg-fill-alt transition-colors cursor-pointer"
            >
              <span className="w-7 flex-shrink-0 text-sm mr-1">
                <i className="ri-compass-3-line text-label-alt"></i>
              </span>
              한복 취향 찾기
            </Link>
          </div>
        </div>

        {/* Drawer Bottom CTA */}
        <div className="px-5 pb-8 pt-3 border-t border-line-alt flex-shrink-0">
          <Link
            to="/guide"
            onClick={closeMenu}
            className="flex items-center justify-center h-12 rounded-full bg-primary-normal text-static-white text-label1 font-semibold hover:bg-primary-strong transition-colors cursor-pointer whitespace-nowrap"
          >
            내 취향 찾기 시작
            <i className="ri-arrow-right-line ml-1.5"></i>
          </Link>
        </div>
      </div>

      {/* ===== Hero Section ===== */}
      <main className="flex-1">
        <section className="pt-12 pb-6 md:pt-16 md:pb-8 px-5 md:px-6">
          <div className="max-w-[720px] mx-auto text-center">
            <span className="inline-block px-3 py-1 rounded-full bg-fill-normal text-accent-brown text-label2 font-medium mb-4">
              플래너 검증 가이드
            </span>
            <h1 className="font-serif text-title3 md:text-title2 text-label-normal mb-3 leading-snug">
              예복 · 예물 · 한복,
              <br />
              무엇부터 기준을 정리할까요?
            </h1>
            <p className="text-body2 text-label-alt max-w-[460px] mx-auto leading-relaxed">
              현직 웨딩 플래너 12인이 검증한 질문으로
              <br />
              나에게 맞는 준비 방향을 정리해드려요.
            </p>
          </div>
        </section>

        {/* ===== Category Cards ===== */}
        <section className="px-5 md:px-6 pb-14 md:pb-16">
          <div className="max-w-[880px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {categories.map(({ key, config, meta }) => (
                <Link
                  key={key}
                  to={`/guide/${key}`}
                  className="group flex flex-col bg-static-white rounded-2xl border border-line-divider hover:border-line-strong transition-all duration-300 overflow-hidden cursor-pointer"
                >
                  <div className="flex flex-col flex-1 p-5 md:p-5">
                    {/* Header row */}
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-9 h-9 rounded-lg bg-fill-normal flex items-center justify-center flex-shrink-0">
                        <i className={`${meta.icon} text-base text-primary-normal`}></i>
                      </div>
                      <div>
                        <span className="block text-label1 font-semibold text-label-normal">
                          {config.headerTitle}
                        </span>
                        <span className="block text-caption mt-0.5 text-label-assistive">
                          {meta.meta}
                        </span>
                      </div>
                    </div>

                    {/* Main copy */}
                    <p className="font-serif text-headline1 mb-1.5 leading-snug text-label-normal">
                      {config.hubMainCopy}
                    </p>
                    <p className="text-label2 leading-relaxed mb-3.5 whitespace-pre-line text-label-alt">
                      {config.hubDescription}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {config.hubTags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 rounded-full text-caption font-medium whitespace-nowrap bg-fill-neutral-chip text-label-alt"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* CTA row — pushed to bottom */}
                    <div className="flex items-center justify-between pt-3 border-t border-line-divider mt-auto">
                      <span className="text-label2 font-medium text-primary-normal">
                        {meta.cta}
                      </span>
                      <div className="w-7 h-7 rounded-full bg-fill-normal group-hover:bg-primary-normal flex items-center justify-center transition-colors duration-300">
                        <i className="ri-arrow-right-line text-primary-normal group-hover:text-static-white transition-colors duration-300 text-xs"></i>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ===== Why Planner ===== */}
        <section className="px-5 md:px-6 pb-20 md:pb-24">
          <div className="max-w-[880px] mx-auto">
            <h2 className="font-serif text-heading2 text-label-normal text-center mb-5 md:mb-6">
              왜 플래너 기준인가요?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              {[
                {
                  icon: 'ri-user-star-line',
                  title: '실제 상담 경험',
                  desc: '매달 수십 쌍의 예비부부가 고민하는 선택 기준을 질문에 담았어요.',
                },
                {
                  icon: 'ri-question-answer-line',
                  title: '의사결정 설계',
                  desc: '막연한 고민을 구체적인 선택 기준으로 바꿔드려요.',
                },
                {
                  icon: 'ri-store-2-line',
                  title: '인증업체 연결',
                  desc: '정리된 기준에 맞춰 확인해볼 만한 인증업체까지 이어드려요.',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex sm:flex-col items-start sm:items-center gap-3 sm:gap-3 bg-static-white rounded-xl border border-line-divider p-4 sm:p-5 sm:text-center"
                >
                  <div className="w-9 h-9 rounded-lg bg-fill-normal flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-base text-accent-brown`}></i>
                  </div>
                  <div>
                    <h4 className="text-label1 font-semibold text-label-normal mb-0.5">
                      {item.title}
                    </h4>
                    <p className="text-label2 text-label-assistive leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}